import { useTheme } from '@chronosphereio/chrono-ui';
import { tags } from '@lezer/highlight';
import { createTheme } from '@uiw/codemirror-themes';
import { EditorView } from '@uiw/react-codemirror';
import { useMemo } from 'react';

export function useEditorTheme() {
  const { theme } = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const {
    palette: { syntaxHighlighting },
    featureFlags: { panwRebrandStubYear },
  } = theme;

  const legacyOrangeOrGrey = isDarkMode ? theme.palette.designSystem.orange[300] : theme.palette.designSystem.grey[600];
  const legacyBlue = isDarkMode ? theme.palette.designSystem.blue[300] : theme.palette.designSystem.blue[600];
  const legacyGreen = isDarkMode ? theme.palette.designSystem.green[300] : theme.palette.designSystem.green[700];
  const legacyPurple = isDarkMode ? '#A582ED' : '#8658E6';
  const legacyGrey = '#8992B7';

  return useMemo(
    () =>
      createTheme({
        theme: isDarkMode ? 'dark' : 'light',
        styles: [
          {
            tag: tags.bool,
            color: panwRebrandStubYear ? syntaxHighlighting.bool : legacyOrangeOrGrey,
          },
          {
            tag: tags.keyword,
            color: panwRebrandStubYear ? syntaxHighlighting.keyword : legacyPurple,
          },
          {
            tag: tags.comment,
            color: panwRebrandStubYear ? syntaxHighlighting.comment : legacyGrey,
          },
          {
            tag: tags.strong,
            color: panwRebrandStubYear ? syntaxHighlighting.strong : legacyBlue,
            fontWeight: theme.typography.fontWeightBold,
          },
          {
            tag: tags.name,
            color: panwRebrandStubYear ? syntaxHighlighting.name_green : legacyGreen,
          },
        ],
        settings: {
          background: theme.palette.background.default,
          selection: 'Highlight',
          selectionMatch: 'Highlight',
          gutterBackground: theme.palette.background.default,
          gutterBorder: theme.palette.background.default,
          caret: theme.palette.text.legacySecondary,
        },
      }),
    [
      isDarkMode,
      theme,
      panwRebrandStubYear,
      legacyBlue,
      legacyGreen,
      legacyOrangeOrGrey,
      legacyPurple,
      legacyGrey,
      syntaxHighlighting,
    ]
  );
}

export function useAutocompleteTheme(args?: { fontSize?: string; hideIcon?: boolean }) {
  const { fontSize = '.75rem', hideIcon = false } = args ?? {};
  const { theme } = useTheme();
  return useMemo(
    () =>
      EditorView.theme({
        '.cm-tooltip': {
          border: `1px solid ${theme.palette.border.default}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.popover,
          '& .cm-diagnostic': {
            boxShadow: 'none',
          },
          '& .cm-tooltip-section': {
            border: 'none',
            boxShadow: 'none',
            // and not first child
            '&:not(:first-child)': {
              borderTop: `1px solid ${theme.palette.border.bold}`,
              borderRadius: 0,
            },
          },
        },
        'button, .cm-diagnosticAction, .btn': {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.border.bold}`,
          borderRadius: `${theme.shape.borderRadius}px`,
        },
        '.btn': {
          fontSize: `${theme.typography.fontSize}px`,
          padding: `${theme.spacing(0.5)}`,
        },
        '.cm-tooltip-hover': {
          boxShadow: theme.shadows[5],
        },
        '.cm-tooltip.cm-tooltip-autocomplete': {
          border: 'none',
          boxShadow: theme.shadows[5],
          borderRadius: `${theme.shape.borderRadius}px`,
          '& > ul[aria-label="Completions"]': {
            backgroundColor: theme.palette.background.popover,
            borderRadius: `${theme.shape.borderRadius}px`,
            boxSizing: 'border-box',
            padding: `${theme.spacing(1)}`,
            // Default maxHeight is 10em - doubling to allow more options to potentially be seen without scrolling
            maxHeight: '20em',
            '& > li': {
              display: 'flex',
              flexDirection: 'row',
              spacing: theme.spacing(1),
              cursor: 'pointer',
              color: theme.palette.text.primary,
              padding: `0 ${theme.spacing(1)}`,
              margin: 0,
              fontFamily: '"Menlo","Helvetica","Arial","sans-serif"',
              lineHeight: '1.66',
              letterSpacing: '0.03333em',
              '&[aria-selected="true"]': {
                backgroundColor: theme.palette.background.selected,
              },
              '& .cm-completionIcon': {
                display: hideIcon ? 'none' : 'block',
              },
              '&:hover': {
                backgroundColor: theme.palette.background.hover,
              },
              '& .cm-completionLabel': {
                minWidth: '25px',
                fontSize: fontSize ?? '1rem',
                // overflow ellipsis
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 700,
                '&:empty': {
                  display: 'none',
                },
                '&:not(:has(+ .cm-completionDetail))': {
                  fontWeight: 'inherit',
                },
              },
              '& .cm-completionDetail': {
                fontStyle: 'normal',
                fontSize: fontSize ?? '1rem',
                color: '#757575',
                flexGrow: 1,
              },
            },
          },
        },
      }),
    [fontSize, hideIcon, theme]
  );
}
